import React from "react";
import {
  PaziaMultiSelect,
  PaziaSelect,
  PaziaButton,
  IDropdownOption,
} from "components/atoms";
import { RecipientSelectionMode } from "./RecipientSelectorTypes";

interface RecipientSelectionControlsProps {
  selectionMode: RecipientSelectionMode;
  onModeChange: (mode: RecipientSelectionMode) => void;

  // Course selection
  courseOptions: IDropdownOption[];
  coursesLoading: boolean;

  // Course group selection
  courseGroupOptions: IDropdownOption[];
  courseGroupsLoading: boolean;

  // Manual selection
  availableUserOptions: IDropdownOption[];
  pendingRecipients: number[];
  onManualRecipientsChange: (values: string[]) => void;
  onConfirmSelection: () => void;

  // Optional customization
  showModeDescriptions?: boolean;
  customLabels?: {
    manual?: string;
    course?: string;
    coursegroup?: string;
    newsletter?: string;
  };
}

export const RecipientSelectionControls: React.FC<RecipientSelectionControlsProps> = ({
  selectionMode,
  onModeChange,
  courseOptions,
  coursesLoading,
  courseGroupOptions,
  courseGroupsLoading,
  availableUserOptions,
  pendingRecipients,
  onManualRecipientsChange,
  onConfirmSelection,
  showModeDescriptions = true,
  customLabels = {},
}) => {
  const labels = {
    manual: customLabels.manual || "Ręczny wybór",
    course: customLabels.course || "Z kursu",
    coursegroup: customLabels.coursegroup || "Z grupy kursów",
    newsletter: customLabels.newsletter || "Lista newsletterowa",
  };

  return (
    <div className="space-y-4">
      {/* Selection Mode Buttons */}
      <div>
        {showModeDescriptions && (
          <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">Wybierz sposób dodawania odbiorców do emaila:</p>
        )}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <PaziaButton
            type={selectionMode === "manual" ? "primary" : "tertiary"}
            onClick={() => onModeChange("manual")}
            className="w-full"
            btnType="button"
          >
            {labels.manual}
          </PaziaButton>
          <PaziaButton
            type={selectionMode === "course" ? "primary" : "tertiary"}
            onClick={() => onModeChange("course")}
            className="w-full"
            btnType="button"
          >
            {labels.course}
          </PaziaButton>
          <PaziaButton
            type={selectionMode === "coursegroup" ? "primary" : "tertiary"}
            onClick={() => onModeChange("coursegroup")}
            className="w-full"
            btnType="button"
          >
            {labels.coursegroup}
          </PaziaButton>
          <PaziaButton
            type={selectionMode === "newsletter" ? "primary" : "tertiary"}
            onClick={() => onModeChange("newsletter")}
            className="w-full"
            btnType="button"
          >
            {labels.newsletter}
          </PaziaButton>
        </div>

        {/* Mode descriptions */}
        {showModeDescriptions && (
          <div className="mt-3 rounded-md bg-blue-50 p-3 dark:bg-blue-900/20">
            {selectionMode === "manual" && (
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Ręczny wybór:</strong> Indywidualnie wybierasz konkretnych użytkowników z listy. Możesz dodawać
                i usuwać odbiorców według własnych potrzeb.
              </p>
            )}
            {selectionMode === "course" && (
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Z kursu:</strong> Automatycznie dodaje wszystkich uczestników wybranego kursu. Lista odbiorców
                będzie zawierać wszystkich zarejestrowanych na ten kurs.
              </p>
            )}
            {selectionMode === "coursegroup" && (
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Z grupy kursów:</strong> Automatycznie dodaje wszystkich uczestników ze wszystkich kursów w
                wybranej grupie kursów.
              </p>
            )}
            {selectionMode === "newsletter" && (
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Lista newsletterowa:</strong> Wysyłka do wszystkich subskrybentów newslettera sklepu. Lista jest wczytywana automatycznie.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Course Group Selection */}
      {selectionMode === "coursegroup" && (
        <div>
          <PaziaSelect
            name="mode_courseGroup"
            placeholder="Wybierz grupę kursów..."
            options={courseGroupOptions}
            isLoading={courseGroupsLoading}
          />
        </div>
      )}

      {/* Course Selection */}
      {selectionMode === "course" && (
        <div>
          <PaziaSelect
            placeholder="Wybierz kurs..."
            options={courseOptions}
            name="mode_course"
            isLoading={coursesLoading}
          />
        </div>
      )}

      {/* Manual Selection */}
      {selectionMode === "manual" && (
        <div className="space-y-3">
          <PaziaMultiSelect
            label=""
            options={availableUserOptions ?? []}
            placeholder="Wybierz odbiorców..."
            name="pendingRecipients"
            value={pendingRecipients.map((id) => id.toString())}
            onChange={onManualRecipientsChange}
          />
          {pendingRecipients.length > 0 && (
            <PaziaButton type="primary" onClick={onConfirmSelection} className="w-full">
              Dodaj wybranych odbiorców ({pendingRecipients.length})
            </PaziaButton>
          )}
        </div>
      )}

      {/* Newsletter Selection */}
      {selectionMode === "newsletter" && null}
    </div>
  );
};
